import { useMemo } from "react";
import { Card, Progress, Tag } from "antd";
import { useTranslation } from "react-i18next";
import { useFormatos } from "../../../../i18n/formatos";
import { Radar } from "@ant-design/charts";
import { PROGRESSION_CHART_COLORS } from "./chartConstants";
import "./RadarProgressChart.scss";
import { useMovil } from "../../../../hooks/useIsNarrow";

// M10 - Para evaluaciones de 2 dimensiones (ej. STAI Estado-Rasgo) el
// radar geometrico no aplica (necesita 3 vertices minimo). Renderizamos
// un panel de barras horizontales con porcentaje y nivel por dimension.
function TwoDimensionsBars({ dims, compareDims }) {
  const { t } = useTranslation("motricity");
  // El porcentaje sale del formateador: el frances le pone un espacio
  // inseparable delante del signo.
  const { porcentaje } = useFormatos();

  return (
    <div className="revi-radar-chart__bars">
      {dims.map((d, idx) => {
        const pct = d.maxPossible
          ? Math.round((d.score / d.maxPossible) * 100)
          : 0;
        const prev =
          compareDims && compareDims.length > 0
            ? compareDims.find((p) => p.name === d.name)
            : null;
        const prevPct = prev?.maxPossible
          ? Math.round((prev.score / prev.maxPossible) * 100)
          : null;
        const color =
          PROGRESSION_CHART_COLORS[idx % PROGRESSION_CHART_COLORS.length];

        return (
          <div key={d.name} className="revi-radar-chart__dim">
            <div className="revi-radar-chart__dim-header">
              <span className="revi-radar-chart__dim-name">{d.name}</span>
              {d.level ? (
                <Tag color={color} bordered={false}>
                  {d.level}
                </Tag>
              ) : null}
            </div>

            <Progress
              percent={pct}
              strokeColor={color}
              format={(p) =>
                t("charts.radar.puntuacion", {
                  porcentaje: porcentaje(p),
                  score: d.score,
                  max: d.maxPossible,
                })
              }
              size={{ height: 14 }}
              status="active"
            />

            {prevPct !== null ? (
              <div className="revi-radar-chart__dim-prev">
                <Progress
                  percent={prevPct}
                  strokeColor="#bfbfbf"
                  format={(p) =>
                    t("charts.radar.puntuacionAnterior", {
                      porcentaje: porcentaje(p),
                      score: prev.score,
                      max: prev.maxPossible,
                    })
                  }
                  size={{ height: 8 }}
                  showInfo
                />
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

export function RadarProgressChart({ loading, dimensions, compareDimensions }) {
  const movil = useMovil();
  const { t } = useTranslation("motricity");
  const { porcentaje } = useFormatos();
  const dims = useMemo(() => dimensions ?? [], [dimensions]);
  const prevDims = useMemo(() => compareDimensions ?? [], [compareDimensions]);

  const data = useMemo(() => {
    if (dims.length < 3) return [];

    const actualSeries = dims.map((d) => ({
      dimension: d.name,
      value: d.maxPossible
        ? Math.round((d.score / d.maxPossible) * 100)
        : d.score,
      serie: t("charts.radar.seriesCurrent"),
      level: d.level,
    }));

    if (prevDims.length > 0) {
      const prevSeries = prevDims.map((d) => ({
        dimension: d.name,
        value: d.maxPossible
          ? Math.round((d.score / d.maxPossible) * 100)
          : d.score,
        serie: t("charts.radar.seriesPrevious"),
        level: d.level,
      }));
      return [...actualSeries, ...prevSeries];
    }

    return actualSeries;
  }, [dims, prevDims, t]);

  const config = useMemo(
    () => ({
      data,
      xField: "dimension",
      yField: "value",
      colorField: "serie",
      area: { style: { fillOpacity: 0.25 } },
      point: { size: 3 },
      autoFit: true,
      height: 300,
      scale: { x: { padding: 0.5 }, y: { domainMin: 0, domainMax: 100 } },
      axis: {
        x: { grid: true, label: { style: { fontSize: 11 } } },
        y: {
          grid: true,
          gridLineDash: [4, 4],
          label: { formatter: (v) => porcentaje(v) },
        },
      },
      legend: { position: "bottom" },
      tooltip: {
        title: "dimension",
        items: [
          (d) => ({
            name: d.serie,
            // `level` es la interpretacion clinica que devuelve el servidor:
            // es DATO, no interfaz, asi que entra en la frase sin traducir.
            value: d.level
              ? t("charts.radar.valorConNivel", {
                  porcentaje: porcentaje(d.value),
                  nivel: d.level,
                })
              : porcentaje(d.value),
          }),
        ],
      },
    }),
    [data, t, porcentaje],
  );

  // M10 - Solo ocultamos cuando no hay ninguna dimension (escala 1D pura).
  // Con 2 dimensiones renderizamos el panel de barras (STAI Estado/Rasgo).
  // Con >=3 mantenemos el radar geometrico original.
  if (!loading && dims.length === 0) return null;

  /*
    En movil, barras SIEMPRE.

    Un radar de seis ejes en 317 px de ancho, con las etiquetas de dimension a
    once pixeles y los valores solo accesibles pasando el raton por encima, no es
    una grafica peor: es un borron del que no se saca un dato. Las barras dicen
    el nombre de la dimension, su nivel y su valor, escritos.

    No se inventa nada: es el mismo panel de barras que ya se usa para las
    escalas de dos dimensiones, aplicado tambien por ancho de pantalla.
  */
  const useRadar = dims.length >= 3 && !movil;

  return (
    <Card
      title={t("charts.radar.title")}
      loading={loading}
      variant="outlined"
      className="revi-eval-chart-card revi-radar-chart"
    >
      {useRadar ? (
        <Radar {...config} />
      ) : (
        <TwoDimensionsBars dims={dims} compareDims={prevDims} />
      )}
    </Card>
  );
}
